import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router';

import { useTypedParams } from '@sorare/routing';

import { Position, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  MainAsideLayout,
  Section,
} from '@sorare/core/src/components/layout/MainAsideLayout';
import { Header } from '@sorare/core/src/components/seo/Header';
import { Competitions } from '@sorare/core/src/components/seo/Team/Competitions';
import { Manager } from '@sorare/core/src/components/seo/Team/Manager';
import { MoreAboutTeam } from '@sorare/core/src/components/seo/Team/MoreAboutTeam';
import {
  FOOTBALL_COUNTRIES_COUNTRYCODE,
  FOOTBALL_LEAGUES_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { Last11 } from 'components/seo/Last11';
import { countryMessages } from 'pages/seo/countries/utils';

import { GamesNavigator } from './GamesNavigator';
import type {
  FootballNationalTeamOverviewQuery,
  FootballNationalTeamOverviewQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const FOOTBALL_NATIONAL_TEAM_OVERVIEW_QUERY = gql`
  query FootballNationalTeamOverviewQuery($countryCode: String!) {
    football {
      nationalTeam(countryCode: $countryCode) {
        slug
        name
        code
        pictureSecondaryUrl
        founded
        styleGuideColor
        country {
          slug
          code
          name
        }
        dataPartner {
          slug
        }
        latestGames(first: 1) {
          nodes {
            id
          }
        }
        activeMemberships(first: 100) {
          nodes {
            id
            player {
              slug
              position
              displayName
              pictureUrl: pictureUrl(derivative: "avatar")
              country {
                slug
                code
              }
              beforeLastOwningMembership {
                id
                team {
                  slug
                  name
                  code
                  pictureSecondaryUrl
                }
              }
            }
            startDate
          }
        }
        recentDepartures(first: 5) {
          nodes {
            slug
            position
            displayName
            pictureUrl: pictureUrl(derivative: "avatar")
            activeClub {
              slug
              name
              code
              pictureSecondaryUrl
            }
            beforeLastOwningMembership {
              id
              endDate
            }
          }
        }
        activeCompetitions {
          slug
          displayName
          pictureUrl
          released
        }
      }
    }
  }
` as TypedDocumentNode<
  FootballNationalTeamOverviewQuery,
  FootballNationalTeamOverviewQueryVariables
>;

const Overview = () => {
  const { countryCode } = useTypedParams<PageParams>();
  const { formatMessage } = useIntl();

  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { data, loading } = useQuery(FOOTBALL_NATIONAL_TEAM_OVERVIEW_QUERY, {
    skip: !countryCode,
    variables: { countryCode: countryCode || '' },
  });

  const pictureUrl =
    data &&
    `${FRONTEND_ASSET_HOST}/flags/${data.football.nationalTeam.country.code}.svg`;

  useTitleAndDescription(
    metadatas.nationalTeamOverview.title,
    metadatas.nationalTeamOverview.description,
    !!data?.football && {
      country: data.football.nationalTeam.country.name,
    },
    {
      img: pictureUrl,
    }
  );

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data) return null;

  const { nationalTeam } = data.football;

  const displayName = formatMessage(countryMessages.nationalTeam, {
    country: nationalTeam.name,
  });

  const coach = nationalTeam.activeMemberships.nodes.find(
    membership => membership.player.position === Position.Coach
  );

  const releasedActiveCompetitions = nationalTeam.activeCompetitions.filter(
    competition => competition.released
  );

  const blocks = {
    header: (
      <>
        <Header
          id={nationalTeam.slug}
          variant="team"
          displayName={nationalTeam.name}
          color={nationalTeam.styleGuideColor}
          pictureUrl={pictureUrl!}
        />
        <Helmet
          script={[
            {
              type: 'application/ld+json',
              innerHTML: JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'SportsTeam',
                name: nationalTeam.name,
                mainEntityOfPage: generatePath(FOOTBALL_COUNTRIES_COUNTRYCODE, {
                  countryCode: nationalTeam.code,
                }),
                sport: Sport.FOOTBALL,
                image: nationalTeam.pictureSecondaryUrl,
                ...(coach
                  ? {
                      coach: {
                        '@type': 'Person',
                        name: coach.player.displayName,
                      },
                    }
                  : {}),
              }),
            },
          ]}
        />
      </>
    ),
    fixtures: data.football.nationalTeam.latestGames.nodes[0] && (
      <GamesNavigator
        displayName={displayName}
        countryCode={countryCode || ''}
      />
    ),
    last11: data.football.nationalTeam.latestGames.nodes[0] && (
      <Last11
        gameId={data.football.nationalTeam.latestGames.nodes[0].id}
        slug={nationalTeam.slug}
      />
    ),
    moreAbout: (
      <MoreAboutTeam
        displayName={displayName}
        manager={coach?.player.displayName}
      />
    ),
    manager: !!coach && (
      <Manager
        displayName={displayName}
        manager={{
          displayName: coach.player.displayName,
          pictureUrl: coach.player.pictureUrl,
          startDate: coach.startDate,
          country: coach.player.country,
        }}
      />
    ),
    competitions: releasedActiveCompetitions.length ? (
      <Competitions
        competitions={releasedActiveCompetitions.map(competition => ({
          ...competition,
          name: competition.displayName,
          to: generatePath(FOOTBALL_LEAGUES_SLUG, { slug: competition.slug }),
          rank: undefined,
        }))}
      />
    ) : null,
  };

  if (isLaptopAndAbove) {
    return (
      <MainAsideLayout
        header={blocks.header}
        aside={[blocks.competitions, blocks.manager]}
      >
        {blocks.fixtures}
        {blocks.last11}
        {blocks.moreAbout}
      </MainAsideLayout>
    );
  }

  return (
    <Section>
      {blocks.header}
      {blocks.fixtures}
      {blocks.last11}
      {blocks.competitions}
      {blocks.manager}
      {blocks.moreAbout}
    </Section>
  );
};

export default Overview;
